/**
 * Error Logger Utility
 * Story 11.3: Error Handling and User-Friendly Error Messages
 * AC-11.3.3: Developer Error Logging
 *
 * Provides structured error logging with context for debugging.
 * Console logging in dev mode, production-ready hooks for monitoring services.
 */

import { type AppError, type ErrorTypeValue, ErrorType } from './errorUtils';

/**
 * Error log entry structure
 */
export interface ErrorLogEntry {
  timestamp: string;
  level: 'error' | 'warn' | 'info';
  errorType: string;
  message: string;
  userId?: string;
  tenantId?: string;
  requestId?: string;
  path?: string;
  stack?: string;
  details?: unknown;
  userAgent?: string;
}

/**
 * Error context for logging
 */
export interface ErrorContext {
  userId?: string;
  tenantId?: string;
  requestId?: string;
  path?: string;
  action?: string;
}

/**
 * Generate a unique request ID
 */
function generateRequestId(): string {
  return `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get current user context from localStorage (if available)
 */
function getUserContext(): { userId?: string; tenantId?: string } {
  try {
    const userStr = localStorage.getItem('user');
    const tenantStr = localStorage.getItem('tenant');

    const user = userStr ? JSON.parse(userStr) : null;
    const tenant = tenantStr ? JSON.parse(tenantStr) : null;

    return {
      userId: user?.id,
      tenantId: tenant?.id,
    };
  } catch {
    return {};
  }
}

/**
 * Create a structured log entry
 */
function createLogEntry(
  error: Error | AppError | unknown,
  level: ErrorLogEntry['level'],
  context?: ErrorContext
): ErrorLogEntry {
  const { userId, tenantId } = getUserContext();
  const now = new Date();

  let errorType: ErrorTypeValue = ErrorType.UNKNOWN;
  let message = 'Unknown error';
  let stack: string | undefined;

  if (error instanceof Error) {
    message = error.message;
    stack = error.stack;
  } else if (typeof error === 'object' && error !== null) {
    const appError = error as AppError;
    if (appError.type) {
      errorType = appError.type;
      message = appError.message;
    }
  } else if (typeof error === 'string') {
    message = error;
  }

  return {
    timestamp: now.toISOString(),
    level,
    errorType,
    message,
    userId: context?.userId || userId,
    tenantId: context?.tenantId || tenantId,
    requestId: context?.requestId || generateRequestId(),
    path: context?.path || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    stack,
    details: typeof error === 'object' && error !== null && 'details' in error
      ? (error as AppError).details
      : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
}

/**
 * Format log entry for console output
 */
function formatForConsole(entry: ErrorLogEntry): string {
  const parts = [
    `[${entry.timestamp}]`,
    `[${entry.level.toUpperCase()}]`,
    `[${entry.errorType}]`,
    entry.message,
  ];

  if (entry.requestId) {
    parts.push(`(${entry.requestId})`);
  }

  return parts.join(' ');
}

/**
 * Log to console (development mode)
 */
function logToConsole(entry: ErrorLogEntry): void {
  const formatted = formatForConsole(entry);

  switch (entry.level) {
    case 'error':
      console.error(formatted);
      if (entry.stack) {
        console.error('Stack:', entry.stack);
      }
      if (entry.details) {
        console.error('Details:', entry.details);
      }
      break;
    case 'warn':
      console.warn(formatted);
      break;
    case 'info':
      console.info(formatted);
      break;
  }

  // Log full context in grouped format for debugging
  if (import.meta.env.DEV) {
    console.groupCollapsed('Error Context');
    console.table({
      userId: entry.userId,
      tenantId: entry.tenantId,
      requestId: entry.requestId,
      path: entry.path,
    });
    console.groupEnd();
  }
}

/**
 * Send to production monitoring service (Sentry-ready)
 * This is a placeholder that can be connected to Sentry, Datadog, etc.
 */
function logToMonitoringService(entry: ErrorLogEntry): void {
  // Production logging hook
  // Uncomment and configure when Sentry is set up:
  //
  // if (typeof Sentry !== 'undefined') {
  //   Sentry.captureException(new Error(entry.message), {
  //     level: entry.level === 'error' ? 'error' : entry.level === 'warn' ? 'warning' : 'info',
  //     tags: {
  //       errorType: entry.errorType,
  //       requestId: entry.requestId,
  //     },
  //     user: {
  //       id: entry.userId,
  //     },
  //     extra: {
  //       tenantId: entry.tenantId,
  //       path: entry.path,
  //       details: entry.details,
  //     },
  //   });
  // }

  // For now, store in sessionStorage for debugging
  if (typeof sessionStorage !== 'undefined') {
    try {
      const existing = sessionStorage.getItem('error_logs');
      const logs: ErrorLogEntry[] = existing ? JSON.parse(existing) : [];
      logs.push(entry);
      // Keep only last 50 errors
      if (logs.length > 50) {
        logs.shift();
      }
      sessionStorage.setItem('error_logs', JSON.stringify(logs));
    } catch {
      // Ignore storage errors
    }
  }
}

/**
 * Main error logging function
 */
export function logError(
  error: Error | AppError | unknown,
  context?: ErrorContext
): ErrorLogEntry {
  const entry = createLogEntry(error, 'error', context);

  // Development: Console logging
  if (import.meta.env.DEV) {
    logToConsole(entry);
  }

  // Production: Monitoring service
  if (!import.meta.env.DEV) {
    logToMonitoringService(entry);
  }

  // Always store for later retrieval
  logToMonitoringService(entry);

  return entry;
}

/**
 * Log a warning
 */
export function logWarning(
  message: string,
  details?: unknown,
  context?: ErrorContext
): ErrorLogEntry {
  const entry = createLogEntry({ type: ErrorType.UNKNOWN, message, details } as AppError, 'warn', context);

  if (import.meta.env.DEV) {
    logToConsole(entry);
  }

  return entry;
}

/**
 * Log an info message
 */
export function logInfo(
  message: string,
  details?: unknown,
  context?: ErrorContext
): ErrorLogEntry {
  const entry = createLogEntry({ type: ErrorType.UNKNOWN, message, details } as AppError, 'info', context);

  if (import.meta.env.DEV) {
    logToConsole(entry);
  }

  return entry;
}

/**
 * Get all stored error logs (for debugging)
 */
export function getStoredErrorLogs(): ErrorLogEntry[] {
  if (typeof sessionStorage === 'undefined') return [];

  try {
    const logs = sessionStorage.getItem('error_logs');
    return logs ? JSON.parse(logs) : [];
  } catch {
    return [];
  }
}

/**
 * Clear stored error logs
 */
export function clearStoredErrorLogs(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('error_logs');
  }
}

/**
 * Create a logger instance with pre-set context
 */
export function createErrorLogger(context: ErrorContext) {
  return {
    error: (error: Error | AppError | unknown) => logError(error, context),
    warn: (message: string, details?: unknown) => logWarning(message, details, context),
    info: (message: string, details?: unknown) => logInfo(message, details, context),
  };
}
